import { XMarkIcon } from '@heroicons/react/24/outline';
import { PlusIcon } from '@heroicons/react/24/solid';
import React, { useEffect, useState, useCallback, type FC } from 'react';

import { TemplateAPIs, TemplateTagAPIs } from '@/api';
import { useTagQuery } from '@/api/queries/tag';
import type { Tag, TagWithCount, TemplateListItem, TemplateTag } from '@/api/types';
import { Button, ButtonSize, ButtonType } from '@/common/button';
import { CheckIcon } from '@/common/check-icon';
import { CreateTagModal } from '@/common/create-tag-modal';
import { Modal } from '@/common/modal';
import { UncheckIcon } from '@/common/uncheck-icon';

interface EditTemplateTagModalProps {
    onDone?: () => void;
    template: TemplateListItem;
    isOpen: boolean;
    setTags: (tags: TemplateTag[]) => void;
    setIsOpen: (isOpen: boolean) => void;
}

export const EditTemplateTagModal: FC<EditTemplateTagModalProps> = ({
    onDone,
    isOpen,
    setIsOpen,
    template,
    setTags,
}) => {
    const [tagList, setTagList] = useState<TagWithCount[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [addTagModalOpen, setAddTagModalOpen] = useState<boolean>(false);

    const { refetch: fetchTags } = useTagQuery({
        onSuccess: (data: TagWithCount[]) => setTagList(data),
        staleTime: Number.POSITIVE_INFINITY,
        cacheTime: 0,
    });

    const getSortedTags = (data: Tag[]) => {
        const appliedTags = data.filter((tag) => template.tags.some((t) => t.tag.id === tag.id));
        const unAppliedTags = data.filter((tag) => !template.tags.some((t) => t.tag.id === tag.id));
        const sorted = [...appliedTags, ...unAppliedTags];
        return sorted;
    };

    const deleteTag = useCallback(
        async (tagId: number) => {
            try {
                await TemplateTagAPIs.deleteTemplateTag(tagId);
                const tags = await TemplateAPIs.getTemplateTags(template.id);
                setTags(tags);
            } catch (error) {
                console.error('Error deleting tag:', error);
            }
        },
        [template, setTags],
    );

    useEffect(() => {
        fetchTags();
    }, [fetchTags]);

    const addTag = useCallback(
        async (tagId: number) => {
            try {
                await TemplateTagAPIs.createTemplateTag(template.id, tagId);
                const tags = await TemplateAPIs.getTemplateTags(template.id);
                setTags(tags);
            } catch (error) {
                console.error('Error adding tag:', error);
            }
        },
        [template, setTags],
    );

    const toggleTag = (templateTag: TemplateTag | undefined, tag: Tag) => {
        if (templateTag) {
            deleteTag(templateTag.id);
        } else {
            addTag(tag.id);
        }
    };

    return (
        <div>
            <Modal isOpen={isOpen}>
                <div className="flex flex-col absolute min-w-64 w-[30vw] max-w-[650px] bg-gray-150 shadow-xl rounded-xl transition-all duration-300 ease-in-out">
                    <div className="flex flex-row justify-between p-4 items-center">
                        <p className="text-xl text-white">Edit Tags</p>
                        <XMarkIcon
                            className="h-5 w-5 text-white hover:cursor-pointer hover:text-gray-30"
                            onClick={isLoading ? () => {} : () => setIsOpen(false)}
                        />
                    </div>
                    <hr className="bg-gray-100 border-gray-120" />
                    <div className="flex flex-col">
                        <div className="flex flex-col max-h-[40vh] overflow-y-auto no-scrollbar">
                            {getSortedTags(tagList).map((tag, index) => {
                                const selectedTag = template.tags.find((t) => t.tag.id === tag.id);
                                return (
                                    <div key={`${Date.now().toString()}${index}`} className="flex flex-col w-full">
                                        <div
                                            className="flex flex-row p-3 gap-2 items-center w-full hover:bg-gray-140 cursor-pointer"
                                            onClick={() => toggleTag(selectedTag, tag)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' || e.key === ' ') {
                                                    toggleTag(selectedTag, tag);
                                                }
                                            }}
                                        >
                                            {selectedTag ? <CheckIcon /> : <UncheckIcon />}
                                            <p className="text-base text-white">{tag.name}</p>
                                        </div>
                                        <hr className="bg-gray-100 border-gray-120 opacity-20 mt-1" />
                                    </div>
                                );
                            })}
                        </div>
                        <div className="p-3">
                            <button
                                type="button"
                                className="group flex flex-row items-center gap-1.5 w-full py-2 text-blue-30 hover:text-blue-50 text-xs"
                                onClick={() => setAddTagModalOpen(true)}
                            >
                                <PlusIcon className="h-5 w-5 text-blue-30 group-hover:stroke-blue-50" />
                                Create New Tag
                            </button>
                        </div>
                        <hr className="mt-2 bg-gray-100 border-gray-120" />
                        <div className="w-full flex flex-row justify-end gap-3 p-6">
                            <Button
                                title="Cancel"
                                className="hover:bg-gray-160"
                                buttonType={ButtonType.SECONDARY}
                                buttonSize={ButtonSize.LARGE}
                                disabled={isLoading}
                                onClick={() => setIsOpen(false)}
                            />
                            <Button
                                title="Done"
                                buttonType={ButtonType.PRIMARY}
                                buttonSize={ButtonSize.LARGE}
                                disabled={isLoading}
                                onClick={() => {
                                    onDone?.();
                                    setIsOpen(false);
                                }}
                            />
                        </div>
                    </div>
                </div>
            </Modal>
            <CreateTagModal isOpen={addTagModalOpen} setIsOpen={setAddTagModalOpen} />
        </div>
    );
};
